/** Toast-уведомления: провайдер + хук useToast(). Контейнер — правый нижний угол. */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { uuid } from "../lib/uuid";
import Icon, { type IconName } from "./Icon";

type ToastKind = "success" | "error" | "info";

interface Toast {
  id: string;
  kind: ToastKind;
  text: string;
}

const ToastCtx = createContext<{ push: (kind: ToastKind, text: string) => void }>({
  push: () => {},
});

// Модульный sink: позволяет слать toast извне React-дерева
// (например, из MutationCache.onError в main.tsx).
let sink: ((kind: ToastKind, text: string) => void) | null = null;

export function pushGlobalToast(kind: ToastKind, text: string) {
  sink?.(kind, text);
}

export function useToast() {
  const { push } = useContext(ToastCtx);
  return {
    success: (text: string) => push("success", text),
    error: (text: string) => push("error", text),
    info: (text: string) => push("info", text),
  };
}

const KIND_STYLE: Record<ToastKind, string> = {
  success: "border-success-line bg-success-soft text-success-ink",
  error: "border-danger-line bg-danger-soft text-danger-ink",
  info: "border-info-line bg-info-soft text-info-ink",
};
const KIND_ICON: Record<ToastKind, IconName> = {
  success: "checkCircle",
  error: "alert",
  info: "info",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((kind: ToastKind, text: string) => {
    const id = uuid();
    setToasts((prev) => [...prev.slice(-3), { id, kind, text }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  useEffect(() => {
    sink = push;
    return () => {
      sink = null;
    };
  }, [push]);

  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-toast flex flex-col items-end gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={`pointer-events-auto flex max-w-sm animate-slide-in-right items-start gap-2 rounded-lg border px-3 py-2.5 text-sm shadow-lg ${KIND_STYLE[t.kind]}`}
          >
            <Icon name={KIND_ICON[t.kind]} size={15} className="mt-px shrink-0" />
            <span className="leading-snug">{t.text}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
